/**
 * validationEngine.js
 * Validates cell values against metadata-driven validation rules.
 * Supports: required, numeric, date, regex, min/max, formula, cross-column.
 */

// ─── Rule Runners ─────────────────────────────────────────────────────────────

const RULES = {
  required(value, rule) {
    if (!rule) return null;
    const empty = value === null || value === undefined || value === '';
    return empty ? (rule.message || 'This field is required') : null;
  },

  min(value, rule) {
    if (rule === undefined || value === '' || value == null) return null;
    return Number(value) < Number(rule.value ?? rule)
      ? (rule.message || `Minimum value is ${rule.value ?? rule}`)
      : null;
  },

  max(value, rule) {
    if (rule === undefined || value === '' || value == null) return null;
    return Number(value) > Number(rule.value ?? rule)
      ? (rule.message || `Maximum value is ${rule.value ?? rule}`)
      : null;
  },

  minLength(value, rule) {
    if (!value) return null;
    return String(value).length < (rule.value ?? rule)
      ? (rule.message || `Minimum length is ${rule.value ?? rule}`)
      : null;
  },

  maxLength(value, rule) {
    if (!value) return null;
    return String(value).length > (rule.value ?? rule)
      ? (rule.message || `Maximum length is ${rule.value ?? rule}`)
      : null;
  },

  pattern(value, rule) {
    if (!value && !rule.required) return null;
    const regex = new RegExp(rule.value ?? rule);
    return !regex.test(String(value))
      ? (rule.message || `Invalid format`)
      : null;
  },

  numeric(value, rule) {
    if (value === '' || value == null) return null;
    return isNaN(Number(value)) ? (rule?.message || 'Must be a number') : null;
  },

  integer(value, rule) {
    if (value === '' || value == null) return null;
    return !Number.isInteger(Number(value)) ? (rule?.message || 'Must be an integer') : null;
  },

  date(value, rule) {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? (rule?.message || 'Invalid date') : null;
  },

  email(value, rule) {
    if (!value) return null;
    return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ? (rule?.message || 'Invalid email address')
      : null;
  },

  oneOf(value, rule) {
    const options = rule.values ?? rule;
    return !options.includes(value)
      ? (rule.message || `Must be one of: ${options.join(', ')}`)
      : null;
  },
};

// ─── Main Validation Engine ───────────────────────────────────────────────────

class ValidationEngine {
  /**
   * Validate a single cell value against column validation metadata.
   * @param {*}      value     - The cell value
   * @param {Object} colMeta   - Column metadata including .validation
   * @param {Object} rowData   - Full row data for cross-column rules
   * @returns {string|null}    - Error message or null if valid
   */
  validateCell(value, colMeta, rowData = {}) {
    const vRules = colMeta?.validation;
    if (!vRules) return null;

    for (const [ruleName, ruleConfig] of Object.entries(vRules)) {
      if (ruleName === 'crossColumn') continue; // handled separately

      const runner = RULES[ruleName];
      if (!runner) continue;

      const error = runner(value, ruleConfig);
      if (error) return error;
    }

    // Cross-column validation
    if (vRules.crossColumn) {
      const error = this._runCrossColumnRule(value, vRules.crossColumn, rowData);
      if (error) return error;
    }

    return null;
  }

  /**
   * Validate an entire row against all column validation rules.
   * @returns {Object} { fieldName: errorMessage, ... }
   */
  validateRow(rowData, columns) {
    const errors = {};

    columns.forEach((col) => {
      const error = this.validateCell(rowData[col.field], col, rowData);
      if (error) errors[col.field] = error;
    });

    return errors; // Empty object = valid
  }

  /**
   * Validate all rows in a dataset.
   * @returns {Map} rowIndex → { fieldName: errorMessage }
   */
  validateAll(rowsData, columns) {
    const errorMap = new Map();

    rowsData.forEach((row, idx) => {
      const errors = this.validateRow(row, columns);
      if (Object.keys(errors).length > 0) errorMap.set(idx, errors);
    });

    return errorMap;
  }

  /**
   * Check if a formula string is syntactically valid.
   * Light check without HF (use formulaWorkerBridge.validateFormula for full check).
   */
  isValidFormulaString(value) {
    if (typeof value !== 'string') return false;
    if (!value.startsWith('=')) return false;
    if (value.length < 2) return false;
    // Basic: no unclosed parens
    let depth = 0;
    for (const ch of value) {
      if (ch === '(') depth++;
      if (ch === ')') depth--;
      if (depth < 0) return false;
    }
    return depth === 0;
  }

  // ── Private ────────────────────────────────────────────────────────────────

  _runCrossColumnRule(value, rule, rowData) {
    /**
     * rule: { dependsOn: 'fieldName', condition: 'greaterThan', message: '...' }
     */
    const depValue = rowData[rule.dependsOn];
    if (depValue === undefined) return null;

    const v   = Number(value);
    const dep = Number(depValue);

    switch (rule.condition) {
      case 'greaterThan':   return v <= dep  ? (rule.message || `Must be greater than ${rule.dependsOn}`) : null;
      case 'lessThan':      return v >= dep  ? (rule.message || `Must be less than ${rule.dependsOn}`) : null;
      case 'greaterOrEqual':return v < dep   ? (rule.message || `Must be ≥ ${rule.dependsOn}`) : null;
      case 'lessOrEqual':   return v > dep   ? (rule.message || `Must be ≤ ${rule.dependsOn}`) : null;
      case 'notEqual':      return v === dep ? (rule.message || `Must not equal ${rule.dependsOn}`) : null;
      default:              return null;
    }
  }
}

const validationEngine = new ValidationEngine();
export default validationEngine;
